import React, { useState, useRef, useEffect } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import type { Company } from '../contexts/CompanyContext';
import CompanySwitcher from './CompanySwitcher';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';

const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

const CompanySelector: React.FC = () => {
  const { t } = useI18n();
  const { currentCompany, companies, setCurrentCompany, isLoading } = useCompany();
  const [isOpen, setIsOpen] = useState(false);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCompanies = (Array.isArray(companies) ? companies : []).filter(company =>
    company.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectCompany = async (company: Company) => {
    try {
      await setCurrentCompany(company);
      setIsOpen(false);
      setSearchTerm('');
      // Nota: A troca de empresa requer novo login com o company_id correto
      // Por enquanto, apenas atualizamos o contexto local
      // Em uma implementação futura, podemos fazer refresh do token
      console.info('[CompanySelector] Empresa alterada para:', company.name);
    } catch (error) {
      console.error('Erro ao trocar empresa:', error);
    }
  };

  const handleOpenSwitcher = () => {
    setIsOpen(false);
    setShowSwitcher(true);
  };

  if (isLoading) {
    return (
      <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '14px' }}>{t('common.loading')}</div>
    );
  }

  // Se não há empresas, mostrar mensagem
  if (companies.length === 0) {
    return (
      <div style={{ padding: '8px 12px', color: '#6b7280', fontSize: '14px' }}>{t('company.noneAvailable')}</div>
    );
  }

  // Se não há empresa atual mas há empresas, usar a primeira
  const displayCompany = currentCompany || companies[0];
  
  if (!displayCompany) {
    return null;
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: '#f3f4f6',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          color: '#111827',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#e5e7eb';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#f3f4f6';
        }}
      >
        <Building2 size={16} />
        <span style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayCompany.name}
        </span>
        <ChevronDown size={16} style={{ opacity: 0.6 }} />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            minWidth: '280px',
            maxWidth: '400px',
            maxHeight: '400px',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Busca */}
          <div style={{ padding: '12px', borderBottom: '1px solid #e5e7eb', position: 'relative' }}>
            <label htmlFor="company-selector-search" style={srOnlyStyle}>{t('company.search')}</label>
            <input
              id="company-selector-search"
              name="company-search"
              type="text"
              placeholder={t('company.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none',
              }}
              autoFocus
            />
          </div>

          {/* Lista de empresas */}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {filteredCompanies.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>{t('company.notFound')}</div>
            ) : (
              filteredCompanies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSelectCompany(company)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    textAlign: 'left',
                    border: 'none',
                    backgroundColor: company.id === displayCompany.id ? '#eff6ff' : 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    if (company.id !== displayCompany.id) {
                      e.currentTarget.style.backgroundColor = '#f9fafb';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (company.id !== displayCompany.id) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', fontSize: '14px', color: '#111827' }}>
                      {company.name}
                    </div>
                    {company.document && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        {company.document}
                      </div>
                    )}
                  </div>
                  {company.id === displayCompany.id && (
                    <Check size={16} style={{ color: '#2563eb' }} />
                  )}
                </button>
              ))
            )}
          </div>
          
          {/* Botão para abrir modal completo se houver muitas empresas */}
          {companies.length > 3 && (
            <div style={{ padding: '8px', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={handleOpenSwitcher}
                style={{
                  width: '100%',
                  padding: '8px',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#2563eb',
                  fontWeight: '500',
                }}
              >
                Ver todas as empresas ({companies.length})
              </button>
            </div>
          )}
        </div>
      )}
      
      <CompanySwitcher isOpen={showSwitcher} onClose={() => setShowSwitcher(false)} />
    </div>
  );
};

export default CompanySelector;

